import React from "react";
import Banner from "../../components/Banner";
import Footer from "../../components/Footer";
import { Sidebar } from "../../components/Sidebar";
import axios from "axios";
import Spinner from "../../components/Spinner";
import { Modal } from "../../components/Modal";

const API = localStorage.getItem("assethost");

// Currency options based on the backend enum
const CURRENCY_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "USD", label: "USD" },
  { value: "ZIG", label: "ZIG" },
];

// Active status options based on the backend enum
const ACTIVE_STATUS_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
];

export default class SubscriptionSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // List state
      subscriptions: [],
      // Pagination
      page: 0,
      size: 10,
      totalPages: 1,
      // Editing state
      editing: false,
      currentSubscriptionIndex: -1,
      subscriptionId: 0,
      subscriptionName: "",
      subscriptionCost: "",
      subscriptionCurrency: "USD",
      subscriptionExpiryInDays: "",
      subscriptionActiveStatus: "YES",
      // UI state
      loading: true,
      valid: true,
      msg: "",
      toastColor: "",
      errors: {},
      // Modal state
      showEditModal: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleCancelEdit = this.handleCancelEdit.bind(this);
    this.validate = this.validate.bind(this);
    this.fetchSubscriptions = this.fetchSubscriptions.bind(this);
    this.fetchSubscriptionById = this.fetchSubscriptionById.bind(this);
  }

  componentDidMount() {
    const token = sessionStorage.getItem("token");
    if (!token) {
      this.setState({
        loading: false,
        msg: "No user session/token. Please login.",
        toastColor: "danger",
      });
      sessionStorage.clear();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    this.fetchSubscriptions(0, 10, token);
  }

  fetchSubscriptions(page, size, token) {
    this.setState({ loading: true, msg: "", toastColor: "" });
    axios
      .get(API + "/billing/getallsubscriptions", {
        params: { page, size },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        let subscriptions = [];
        let totalPages = 1;
        if (Array.isArray(response.data)) {
          subscriptions = response.data;
        } else if (
          response.data &&
          response.data.content &&
          Array.isArray(response.data.content)
        ) {
          subscriptions = response.data.content;
          totalPages = response.data.totalPages || 1;
        } else {
          subscriptions = [];
        }
        this.setState({
          subscriptions,
          page,
          totalPages,
          loading: false,
        });
      })
      .catch((error) => {
        if (Number(error.status) === 403) {
          sessionStorage.clear();
          window.location.href = `/?redirectUrl=${
            window.location.pathname + window.location.search
          }`;
        }
        this.setState({
          subscriptions: [],
          totalPages: 1,
          loading: false,
          msg:
            error?.response?.data?.message || "Failed to load subscriptions.",
          toastColor: "danger",
        });
      });
  }

  fetchSubscriptionById(id, token) {
    this.setState({ loading: true, msg: "", toastColor: "" });
    axios
      .get(`${API}/billing/getsubscriptionbyid/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data) {
          this.setState({
            subscriptionId: response.data.id || 0,
            subscriptionName: response.data.name || "",
            subscriptionCost: response.data.cost || "",
            subscriptionCurrency: response.data.currency || "USD",
            subscriptionExpiryInDays: response.data.expiryInDays || "",
            subscriptionActiveStatus: response.data.activeStatus || "YES",
            loading: false,
          });
        }
      })
      .catch((error) => {
        if (Number(error.status) === 403) {
          sessionStorage.clear();
          window.location.href = `/?redirectUrl=${
            window.location.pathname + window.location.search
          }`;
        }
        this.setState({
          loading: false,
          msg:
            error?.response?.data?.message ||
            `Failed to load subscription ${id}.`,
          toastColor: "danger",
        });
      });
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({ [name]: value }, this.validate);
  }

  validate() {
    const s = this.state;
    let errors = {};
    let valid = true;

    if (s.editing || s.showEditModal) {
      // Subscription validation
      if (!s.subscriptionName || !s.subscriptionName.trim()) {
        errors.subscriptionName = "Subscription name is required";
        valid = false;
      }
      if (
        !s.subscriptionCost ||
        isNaN(s.subscriptionCost) ||
        parseFloat(s.subscriptionCost) < 0
      ) {
        errors.subscriptionCost =
          "Valid cost is required (must be a positive number)";
        valid = false;
      }
      if (!s.subscriptionCurrency || !s.subscriptionCurrency.trim()) {
        errors.subscriptionCurrency = "Currency is required";
        valid = false;
      }
      if (
        !s.subscriptionExpiryInDays ||
        isNaN(s.subscriptionExpiryInDays) ||
        parseInt(s.subscriptionExpiryInDays) <= 0
      ) {
        errors.subscriptionExpiryInDays =
          "Valid expiry days is required (must be a positive number)";
        valid = false;
      }
      if (!s.subscriptionActiveStatus || !s.subscriptionActiveStatus.trim()) {
        errors.subscriptionActiveStatus = "Active status is required";
        valid = false;
      }
    }
    this.setState({ errors, valid });
    return valid;
  }

  handleSave(e) {
    if (e) e.preventDefault();
    const token = sessionStorage.getItem("token");
    if (!token) {
      this.setState({
        msg: "No user session/token. Please login.",
        toastColor: "danger",
      });
      sessionStorage.clear();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    if (!this.validate()) {
      this.setState({
        msg: "Fix the errors above and try again.",
        toastColor: "danger",
      });
      return;
    }
    this.setState({ loading: true, msg: "", toastColor: "" });

    // Build subscription object
    const request = {
      id: this.state.subscriptionId,
      name: this.state.subscriptionName,
      cost: parseFloat(this.state.subscriptionCost),
      currency: this.state.subscriptionCurrency,
      expiryInDays: parseInt(this.state.subscriptionExpiryInDays),
      activeStatus: this.state.subscriptionActiveStatus,
    };

    const self = this;
    axios
      .post(API + "/billing/subscriptionsave", request, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success) {
          self.setState({
            msg: response.data.message || "Subscription saved successfully!",
            toastColor: "success",
            loading: false,
          });
          // Reset form
          self.setState({
            subscriptionId: 0,
            subscriptionName: "",
            subscriptionCost: "",
            subscriptionCurrency: "USD",
            subscriptionExpiryInDays: "",
            subscriptionActiveStatus: "YES",
            editing: false,
            showEditModal: false,
            errors: {},
          });
          // Refetch data
          self.fetchSubscriptions(self.state.page, self.state.size, token);
        } else {
          self.setState({
            msg: response.data.message || "Failed to save subscription.",
            toastColor: "danger",
            loading: false,
          });
        }
      })
      .catch((error) => {
        if (Number(error.status) === 403) {
          sessionStorage.clear();
          window.location.href = `/?redirectUrl=${
            window.location.pathname + window.location.search
          }`;
        }
        self.setState({
          msg:
            error?.response?.data?.message ||
            "Failed to save subscription. Please try again.",
          toastColor: "danger",
          loading: false,
        });
      });
  }

  handleEdit(subscription) {
    if (subscription && subscription.id) {
      this.setState({
        subscriptionId: subscription.id || 0,
        subscriptionName: subscription.name || "",
        subscriptionCost: subscription.cost || "",
        subscriptionCurrency: subscription.currency || "",
        subscriptionExpiryInDays: subscription.expiryInDays || "",
        subscriptionActiveStatus: subscription.activeStatus || "",
        editing: true,
        errors: {},
        showEditModal: true,
      });
    } else {
      // New subscription
      this.setState(
        {
          subscriptionId: 0,
          subscriptionName: "",
          subscriptionCost: "",
          subscriptionCurrency: "",
          subscriptionExpiryInDays: "",
          subscriptionActiveStatus: "",
          editing: true,
          errors: {},
          showEditModal: true,
        },
        this.validate
      );
    }
  }

  handleCancelEdit() {
    this.setState({
      editing: false,
      showEditModal: false,
      subscriptionId: 0,
      subscriptionName: "",
      subscriptionCost: "",
      subscriptionCurrency: "",
      subscriptionExpiryInDays: "",
      subscriptionActiveStatus: "",
      errors: {},
    });
  }

  handlePreviousPage() {
    if (this.state.page > 0) {
      const token = sessionStorage.getItem("token");
      this.fetchSubscriptions(this.state.page - 1, this.state.size, token);
    }
  }

  handleNextPage() {
    if (this.state.page + 1 < this.state.totalPages) {
      const token = sessionStorage.getItem("token");
      this.fetchSubscriptions(this.state.page + 1, this.state.size, token);
    }
  }

  render() {
    const s = this.state;
    let divMsg = null;
    if (s.msg) {
      divMsg = (
        <div
          className={
            s.toastColor === "success"
              ? "alert alert-success mx-7"
              : "alert alert-danger mx-7"
          }
        >
          {s.msg}
        </div>
      );
    }

    if (s.loading) {
      return <Spinner className="" />;
    }

    return (
      <div className="min-h-screen h-dvh flex justify-between bg-white">
        <Sidebar />
        <div className="min-h-screen w-[85%] flex flex-col">
          <Banner title="Subscription Settings" />
          {divMsg}
          <div className="w-full grow px-5 h-[60%]">
            <div className="h-full w-full">
              <div className="flex gap-2 mb-2 items-baseline justify-between">
                <h3 className="font-bold">Configured Subscriptions</h3>
                <button
                  className="hover:font-bold flex gap-1 group"
                  onClick={() => this.handleEdit({})}
                >
                  <i
                    className="bi bi-plus-circle group-hover:scale-105"
                    style={{ color: "black" }}
                  ></i>
                  Add
                </button>
              </div>
              {s.subscriptions.length === 0 ? (
                <div className="text-gray-500">No subscriptions found.</div>
              ) : (
                <div
                  className={`overflow-x-auto w-full border relative overflow-y-auto ${
                    s.totalPages > 1 ? "h-[83%]" : "h-[93%]"
                  }`}
                >
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Currency
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expiry (Days)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {s.subscriptions.map((subscription, index) => (
                        <tr
                          key={`subscription-row-${subscription.id}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.cost}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.currency}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.expiryInDays}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.activeStatus}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              className="btn btn-success"
                              onClick={() => {
                                this.setState({
                                  showEditModal: true,
                                  subscriptionId: subscription.id,
                                  subscriptionName: subscription.name,
                                  subscriptionCost: subscription.cost,
                                  subscriptionCurrency: subscription.currency,
                                  subscriptionExpiryInDays:
                                    subscription.expiryInDays,
                                  subscriptionActiveStatus:
                                    subscription.activeStatus,
                                });
                              }}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {s.totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-2">
                  <button
                    className="btn btn-outline"
                    disabled={s.page === 0}
                    onClick={this.handlePreviousPage}
                  >
                    Previous
                  </button>
                  <span>
                    Page {s.page + 1} of {s.totalPages}
                  </span>
                  <button
                    className="btn btn-outline"
                    disabled={s.page + 1 >= s.totalPages}
                    onClick={this.handleNextPage}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>

        {/* Edit Subscription Modal */}
        {s.showEditModal && (
          <Modal
            title="Manage Subscription"
            closeAction={() => this.setState({ showEditModal: false })}
            footerContent={
              <>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={s.loading}
                  form="subscriptionForm"
                  onClick={this.handleSave}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() =>
                    this.setState({
                      valid: false,
                      subscriptionName: "",
                      subscriptionCost: "",
                      subscriptionCurrency: "USD",
                      subscriptionExpiryInDays: "",
                      subscriptionActiveStatus: "YES",
                      errors: {},
                      showEditModal: false,
                    })
                  }
                >
                  Close
                </button>
              </>
            }
          >
            <form
              autoComplete="off"
              id="subscriptionForm"
              onSubmit={this.handleSave}
            >
              <div className="row">
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">
                      Subscription Name
                    </label>
                    <input
                      name="subscriptionName"
                      className={`form-control ${
                        s.errors.subscriptionName ? " !border-red-500" : ""
                      }`}
                      value={s.subscriptionName}
                      onChange={this.handleChange}
                      required
                      autoFocus
                    />
                    {s.errors.subscriptionName && (
                      <span className="input-group-error">
                        {s.errors.subscriptionName}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">Cost</label>
                    <input
                      name="subscriptionCost"
                      type="number"
                      step="0.01"
                      min="0"
                      className={`form-control ${
                        s.errors.subscriptionCost ? " !border-red-500" : ""
                      }`}
                      value={s.subscriptionCost}
                      onChange={this.handleChange}
                      required
                      placeholder="0.00"
                    />
                    {s.errors.subscriptionCost && (
                      <span className="input-group-error">
                        {s.errors.subscriptionCost}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">Currency</label>
                    <select
                      name="subscriptionCurrency"
                      className={`form-control ${
                        s.errors.subscriptionCurrency ? " !border-red-500" : ""
                      }`}
                      value={s.subscriptionCurrency}
                      onChange={this.handleChange}
                      required
                    >
                      {CURRENCY_OPTIONS.map((option) => (
                        <option
                          key={`currency-${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {s.errors.subscriptionCurrency && (
                      <span className="input-group-error">
                        {s.errors.subscriptionCurrency}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">Expiry (Days)</label>
                    <input
                      name="subscriptionExpiryInDays"
                      type="number"
                      min="1"
                      className={`form-control ${
                        s.errors.subscriptionExpiryInDays
                          ? " !border-red-500"
                          : ""
                      }`}
                      value={s.subscriptionExpiryInDays}
                      onChange={this.handleChange}
                      required
                      placeholder="30"
                    />
                    {s.errors.subscriptionExpiryInDays && (
                      <span className="input-group-error">
                        {s.errors.subscriptionExpiryInDays}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="input-group">
                    <label className="input-group-text">Active Status</label>
                    <select
                      name="subscriptionActiveStatus"
                      className={`form-control ${
                        s.errors.subscriptionActiveStatus
                          ? " !border-red-500"
                          : ""
                      }`}
                      value={s.subscriptionActiveStatus}
                      onChange={this.handleChange}
                      required
                    >
                      {ACTIVE_STATUS_OPTIONS.map((option) => (
                        <option
                          key={`status-${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {s.errors.subscriptionActiveStatus && (
                      <span className="input-group-error">
                        {s.errors.subscriptionActiveStatus}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col"></div>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  }
}
